// Shared "is this project an instance of a registered template?" read path
// for Plan B2 Task 4 (cogo.template.v1 content-service loop). Small, additive
// module so both `ChatRail.tsx` (tab visibility gate) and
// `TemplateContentPanel.tsx` (which registry entry to render) share the same
// contract instead of duplicating the `settings.cogoApplicationMetadata.
// instantiatedFrom` read (persisted by `persistProjectTemplateLineage`,
// Plan B1 Task 3).
//
// Plan B2 Task 5 additionally puts the `divergedAt` boundary-exit marking
// hook (`markTemplateDivergenceOnForeignEdit`) here — it's the natural
// counterpart to `readInstantiatedFrom` (same shape, same module), and this
// file has NO import that reaches back into the studio-store's own module
// graph (unlike `auto-save.ts`, which must dynamically import this module —
// see its call site — to avoid closing the studio-store -> data-slice ->
// auto-save cycle documented in `auto-save.ts`'s `projectIdSource` comment).

import { TEMPLATE_REGISTRY, type TemplateRegistryEntry } from '../registry'
import { markProjectTemplateDiverged } from '@/services/project-api'
import { useStudioStore } from '@/store/studio-store'

export interface TemplateLineage {
  templateId: string
  version: string
  structureHash: string
  /** ISO timestamp of the FIRST edit made outside the template content-form
   * (Plan B2 Task 5). Absent until `markTemplateDivergenceOnForeignEdit` (or
   * the server-side `markProjectTemplateDiverged`) stamps it. */
  divergedAt?: string
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Reads `metadata.instantiatedFrom` (the shape `ProjectMeta.metadata`
 * carries — see `persistProjectTemplateLineage` in `services/project-api.ts`)
 * and returns it typed, or `null` when absent/malformed. `metadata` is
 * `ProjectMeta['metadata']` (a loose passthrough `Record<string, unknown>`
 * — there is no dedicated typed field for this on `ProjectMeta` yet).
 */
export function readInstantiatedFrom(metadata: Record<string, unknown> | null | undefined): TemplateLineage | null {
  const raw = metadata?.instantiatedFrom
  if (!isPlainObject(raw)) return null
  const { templateId, version, structureHash, divergedAt } = raw
  if (typeof templateId !== 'string' || typeof version !== 'string' || typeof structureHash !== 'string') return null
  return {
    templateId,
    version,
    structureHash,
    ...(typeof divergedAt === 'string' ? { divergedAt } : {}),
  }
}

/** Looks up `lineage.templateId` in `TEMPLATE_REGISTRY`, or `null` when there's no lineage or no matching entry (e.g. a template later removed from the registry). */
export function resolveTemplateRegistryEntry(lineage: TemplateLineage | null): TemplateRegistryEntry | null {
  if (!lineage) return null
  return TEMPLATE_REGISTRY.find((entry) => entry.pkg.templateId === lineage.templateId) ?? null
}

/**
 * Plan B2 Task 5 — boundary-exit marking. Call once per save that
 * originates OUTSIDE the template content-form for a project that carries
 * template lineage — `apply-slot-values.ts`'s `applySlotValues` brackets its
 * OWN entire validate-through-save sequence with `templateFormApplyInFlight`
 * true, so this must never fire while that flag is true (checked first,
 * before touching anything else).
 *
 * No-ops when: `templateFormApplyInFlight` is true, `projectId` isn't the
 * CURRENTLY open project (`store.projectMeta` only ever describes the
 * current project — a stray/stale queued save for a project the user has
 * since navigated away from must not mark the wrong project's in-store
 * meta), the open project has no template lineage yet (`instantiatedFrom`
 * absent), or the lineage is already diverged (`divergedAt` set).
 *
 * On the first qualifying call: writes `divergedAt` into the in-store
 * `projectMeta` SYNCHRONOUSLY (so a second foreign edit later in the same
 * session sees it already set and short-circuits without a second network
 * round trip), then fires `markProjectTemplateDiverged` fire-and-forget —
 * its own server-side idempotence (it no-ops once `divergedAt` is already
 * persisted) is the backstop for any race this client-side check misses
 * (e.g. two tabs open on the same project). Failures are swallowed to a
 * `console.warn` — a lineage-marking write must never surface as a
 * user-facing save error.
 */
export function markTemplateDivergenceOnForeignEdit(projectId: string): void {
  const store = useStudioStore.getState()
  if (store.templateFormApplyInFlight) return
  if (store.currentProjectId !== projectId) return

  const metadata = (store.projectMeta?.metadata as Record<string, unknown> | undefined) ?? null
  const lineage = readInstantiatedFrom(metadata)
  if (!lineage || lineage.divergedAt) return

  const divergedAt = new Date().toISOString()
  useStudioStore.setState((s) => {
    if (!s.projectMeta) return
    const nextMetadata = { ...((s.projectMeta.metadata as Record<string, unknown> | undefined) ?? {}) }
    const existingLineage = isPlainObject(nextMetadata.instantiatedFrom)
      ? (nextMetadata.instantiatedFrom as Record<string, unknown>)
      : {}
    nextMetadata.instantiatedFrom = { ...existingLineage, divergedAt }
    s.projectMeta.metadata = nextMetadata
  })

  markProjectTemplateDiverged(projectId).catch((err) => {
    console.warn('[project-lineage] Failed to persist divergedAt marker:', err)
  })
}
